import React, { useEffect, useId, useRef, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { normalizeHexColorInput, toPickerHexColor, validateHexColor } from '@/utils/validation'

interface ColorPickerInputProps {
  label?: string
  value: string
  onChange: (value: string) => void
  error?: string
  helperText?: string
  required?: boolean
  placeholder?: string
  fallbackColor?: string
}

export const ColorPickerInput: React.FC<ColorPickerInputProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  required = false,
  placeholder = '#08b2b3',
  fallbackColor = '#08b2b3',
}) => {
  const inputId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [textValue, setTextValue] = useState(value)

  useEffect(() => {
    setTextValue(value)
  }, [value])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const pickerColor = toPickerHexColor(value, fallbackColor)
  const swatchColor = validateHexColor(value) ? value : 'transparent'

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeHexColorInput(e.target.value)
    setTextValue(next)
    onChange(next)
  }

  const handleTextBlur = () => {
    if (!textValue.trim()) {
      onChange('')
      return
    }

    if (validateHexColor(textValue)) {
      onChange(textValue.toLowerCase())
      return
    }

    setTextValue(value)
  }

  const handlePickerChange = (nextColor: string) => {
    const normalized = nextColor.toLowerCase()
    setTextValue(normalized)
    onChange(normalized)
  }

  const stateStyles = error
    ? 'border-[var(--color-error)] focus:border-[var(--color-error)] focus:ring-[var(--color-error)]'
    : 'border-[var(--color-border)] focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)]'

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
        >
          {label}
          {required && <span className="text-[var(--color-error)] ml-1">*</span>}
        </label>
      )}

      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setIsOpen(prev => !prev)}
            className={`w-11 h-11 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-primary)] ${
              error ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
            }`}
            style={{
              backgroundColor: swatchColor,
              backgroundImage: !validateHexColor(value)
                ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                : undefined,
              backgroundSize: !validateHexColor(value) ? '8px 8px' : undefined,
              backgroundPosition: !validateHexColor(value) ? '0 0, 4px 4px' : undefined,
            }}
            aria-label={`Choisir ${label ?? 'une couleur'}`}
            aria-expanded={isOpen}
          />

          {isOpen && (
            <div className="color-picker-popover absolute left-0 top-full z-50 mt-2 rounded-lg border border-[var(--color-border)] bg-white p-3 shadow-lg">
              <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <input
            id={inputId}
            type="text"
            value={textValue}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            placeholder={placeholder}
            required={required}
            className={`w-full px-4 py-2.5 text-base border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${stateStyles}`}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>

      {error && (
        <p className="mt-1.5 text-sm text-[var(--color-error)]" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-[var(--color-text-muted)]">{helperText}</p>
      )}
    </div>
  )
}
